import React from 'react';
import { GetServerSidePropsContext } from 'next';
import LoginRegisterModalProvider
  from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import AddToBoardModalProvider
  from '@components/shared/providers/AddToBoardModalProvider/AddToBoardModalProvider';
import AddedToCartConfirmationModalProvider from '@components/shared/providers/AddedToCartConfirmationModalProvider/AddedToCartConfirmationModalProvider';
import GET_VENDOR_PRODUCT_GROUP_BY_ID
  from '@graphql/VendorVariant/queries/get-vendor-product-group-by-id';
import { withAuthenticatedUser } from '@lib/user/auth';
import { getApollo } from '@lib/apollo/apolloClient';
import ProductGroupV2 from '@components/pages/ProductV2/ProductGroup/ProductGroup';
import logger from '@lib/logger';
import { IAuthResponse } from '@models/shared/i-auth-response';
import { ApolloClient } from '@apollo/client';
import { fetchShopNavigationMenu } from '@components/shared/shop/ShopNavigationMenu';
import Compose from '@components/shared/Compose';
import ShareModalProvider from '@components/shared/providers/ShareModalProvider/ShareModalProvider';
import { getResizedImageUrl } from '@lib/image';
import isAuthedOnlyProduct from '@components/pages/ProductV2/shared/utils/productUtils';
import browser from '../../utils/browser/browser';
import env from '../../lib/envs/env';

export default function ProductGroupPage(props: any) {
  return (
    <Compose
      components={[
        LoginRegisterModalProvider,
        ShareModalProvider,
        AddToBoardModalProvider,
        AddedToCartConfirmationModalProvider
      ]}
    >
      <ProductGroupV2 {...props} />
    </Compose>
  );
}

export const getServerSideProps = withAuthenticatedUser(async ({
  user,
}, ctx: GetServerSidePropsContext) => {
  const log = logger('PGP');
  const { query: { slug, vendorVariantId }, resolvedUrl } = ctx;

  if (!slug || typeof slug !== 'string') {
    return {
      notFound: true,
    };
  }

  const id = getVendorProductGroupIdFromSlug(slug);

  const userAgent = ctx.req.headers['user-agent'] || '';
  const isIosNative = browser.isIosNativeApp(userAgent);
  const apolloClient = getApollo();

  return getServerSidePropsV2(
    apolloClient,
    vendorVariantId,
    resolvedUrl,
    slug,
    user,
    ctx,
    log,
    isIosNative,
    id,
  );
}, {
  requireAuth: false,
});

function getVendorProductGroupIdFromSlug(slug: string): string | undefined {
  // @ts-ignore
  const matches = slug.match(/-(?<vpgId>\d+)$/);
  return matches?.groups?.vpgId;
}

async function getServerSidePropsV2(
  apolloClient: ApolloClient<any>,
  vendorVariantId: string | string[] | undefined,
  resolvedUrl: string,
  slug: string,
  user: IAuthResponse | null,
  ctx: GetServerSidePropsContext,
  log: any,
  isIosNative: boolean,
  id?: string,
) {
  let vendorProductGroupResponse: any;

  try {
    if (id) {
      vendorProductGroupResponse = await apolloClient.query({
        query: GET_VENDOR_PRODUCT_GROUP_BY_ID,
        variables: {
          id,
          attributeFilters: []
        },
      });
    }
  } catch (e) {
    log.error({ slug }, 'Unable to load products');
    throw e;
  }

  if (!vendorProductGroupResponse) {
    log.warn(`Unable to load data from graph`);
    return { notFound: true };
  }

  if (vendorProductGroupResponse.data?.vendorProductGroupById?.vendorProductGrouping?.url) {
    return {
      redirect: {
        destination: vendorProductGroupResponse
          .data
          .vendorProductGroupById
          .vendorProductGrouping
          .url,
        permanent: true,
      },
    };
  }

  const { data: { vendorProductGroupById: vendorProductGroup } } = vendorProductGroupResponse;
  const collectionWithProducts = {
    ctaLabel: 'Shop Now',
    image: {
      alt: 'Best Sellers for a Reason product collection',
      url: getResizedImageUrl({ url: 'https://s3.us-east-1.amazonaws.com/static.havenly.com/product/production/list_page-best_sellers.jpg' }),
    },
    ctaUrl: '/shop/collection/best-sellers-for-a-reason',
    imageTitle: 'Best Sellers for a Reason'
  };

  const userIsAnnonymous = !user;
  const initialVendorVariants: any[] = vendorProductGroup?.vendorVariants || [];

  // Redirect if user is annonymous and any of the products is logged in access only
  if (userIsAnnonymous && initialVendorVariants.some((vv: any) => isAuthedOnlyProduct(vv))) {
    return {
      redirect: {
        destination: `/shop`,
        statusCode: 302,
      },
    };
  }

  if (!vendorProductGroup || !initialVendorVariants.length) {
    return {
      notFound: true,
    };
  }

  const shopNavigationMenu = await fetchShopNavigationMenu({
    previewData: ctx.previewData,
    apolloClient,
  });

  return {
    props: {
      user,
      isIosNative,
      initialVendorVariantId: vendorVariantId || null,
      initialVendorProductGroup: vendorProductGroup,
      legacyGroupPage: true,
      host: `https://${env.DOMAIN_NAME}`,
      canonicalUrl: `https://${env.DOMAIN_NAME}${resolvedUrl.split('?')[0]}`,
      shopNavigationMenu,
      collectionWithProducts,
    },
  };
}
